'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { CategoriesEntity } from '@eliadmin/core/src/api/products/types/product-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { LuX } from 'react-icons/lu';
import { MdArrowCircleRight, MdOutlineKeyboardArrowLeft } from 'react-icons/md';

export function HeaderCategories({ categories }: { categories: CategoriesEntity[] }) {
  const { t } = useTranslations();

  const { isOpen, setIsOpen } = useDisclosure();
  const [isOpenLevel2, setIsOpenLevel2] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoriesEntity | null>(null);

  const handleCloseAll = () => {
    setIsOpenLevel2(false);
    setIsOpen(false);
    setSelectedCategory(null);
  };

  const handleBackToLevel1 = () => {
    setIsOpenLevel2(false);
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open);
        if (!open) {
          setIsOpenLevel2(false);
          setSelectedCategory(null);
        }
      }}
    >
      <SheetTrigger asChild>
        <Button variant={'primary-foreground'}>
          <ButtonIcon alt={t('text3316')} url="/images/icons/element-equal.svg" />
          {t('text3316')}
        </Button>
      </SheetTrigger>
      <SheetContent
        side={'left'}
        className="overflow-visible p-0 sm:max-w-[444px]"
        closeIcon={{ wrapper: 'hidden' }}
      >
        <div className="relative z-10 flex h-full w-full flex-col items-start justify-start gap-12 bg-background p-12">
          <SheetHeader className="flex w-full flex-row items-start justify-between">
            <SheetTitle className="w-full">
              <Heading title={t('text3316')} tag="h3" />
            </SheetTitle>
            <SheetClose onClick={() => handleCloseAll()}>
              <LuX size={24} className="text-[#6E6D7B]" />
            </SheetClose>
          </SheetHeader>
          <div className="hide-scroll-bar flex w-full flex-col items-start justify-start gap-4 overflow-y-scroll">
            {categories.map(category => {
              const hasChilds = category?.childs && category?.childs?.length > 0;

              if (hasChilds) {
                return (
                  <button
                    key={category.slug}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(category);
                      setIsOpenLevel2(true);
                    }}
                    className="group flex w-full items-center justify-between gap-6"
                  >
                    {!!category?.icon?.url && (
                      <div className="flex max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] items-center justify-center rounded-[12px] bg-[#E4F2E7]">
                        <Image
                          src={category?.icon?.url ?? '/images/placeholder.png'}
                          alt={resolveImageAlt(category?.icon, category?.name)}
                          width={28}
                          height={28}
                          className="object-contain"
                        />
                      </div>
                    )}

                    <Heading
                      tag="h5"
                      className={{
                        container: 'w-full flex-1',
                        title: 'text-left transition-all group-hover:text-primary',
                      }}
                      title={category.name}
                    />
                    <MdArrowCircleRight className="max-h-6 min-h-6 min-w-6 max-w-6 text-primary opacity-0 transition-all group-hover:opacity-100" />
                  </button>
                );
              }

              return (
                <SheetClose key={category.slug} asChild onClick={() => handleCloseAll()}>
                  <Link
                    prefetch={false}
                    href={category.slug}
                    className="group flex w-full items-center justify-between gap-6"
                  >
                    {!!category?.icon?.url && (
                      <div className="flex max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] items-center justify-center rounded-[12px] bg-[#E4F2E7]">
                        <Image
                          src={category?.icon?.url ?? '/images/placeholder.png'}
                          alt={resolveImageAlt(category?.icon, category?.name)}
                          width={28}
                          height={28}
                          className="object-contain"
                        />
                      </div>
                    )}
                    <Heading
                      tag="h5"
                      className={{
                        container: 'w-full flex-1',
                        title: 'text-left transition-all group-hover:text-primary',
                      }}
                      title={category.name}
                    />
                    <MdArrowCircleRight className="max-h-6 min-h-6 min-w-6 max-w-6 text-primary opacity-0 transition-all group-hover:opacity-100" />
                  </Link>
                </SheetClose>
              );
            })}
          </div>
        </div>

        <div
          className={cn(
            'absolute left-full top-0 z-0 flex h-full w-full flex-col items-start justify-start gap-12 bg-[#FCFBFF] p-12 shadow-2xl transition-transform duration-300 ease-out sm:max-w-[444px]',
            'ea-header-categories__subcategories-content',
            isOpenLevel2 ? 'translate-x-0' : '-translate-x-full',
          )}
          inert={!isOpenLevel2}
        >
          <div className="flex w-full flex-row items-center justify-between">
            <Link
              href={selectedCategory?.slug ?? '#'}
              onClick={() => handleCloseAll()}
              className="flex items-center justify-start gap-2 underline"
            >
              <Heading title={selectedCategory?.name ?? ''} tag="h4" />
            </Link>
            <button
              type="button"
              onClick={() => handleBackToLevel1()}
              className="flex items-center justify-start gap-2"
            >
              <LuX size={24} className="text-foreground" />
            </button>
          </div>
          <div className="hide-scroll-bar flex w-full flex-col items-start justify-start gap-4 overflow-y-scroll">
            {selectedCategory?.childs?.map(child => (
              <Link
                key={child.slug}
                prefetch={false}
                href={child.slug}
                onClick={() => handleCloseAll()}
                className="group flex w-full items-center justify-between gap-6"
              >
                {!!child?.icon?.url && (
                  <div className="flex max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] items-center justify-center rounded-[12px] bg-[#E4F2E7]">
                    <Image
                      src={child?.icon?.url ?? '/images/placeholder.png'}
                      alt={resolveImageAlt(child?.icon, child?.name)}
                      width={28}
                      height={28}
                      className="object-contain"
                    />
                  </div>
                )}
                <Heading
                  tag="h5"
                  className={{
                    container: 'w-full flex-1',
                    title: 'text-left transition-all group-hover:text-primary',
                  }}
                  title={child.name}
                />
              </Link>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
